import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { IoIosArrowBack } from "react-icons/io";
import { useLocation, useNavigate } from "react-router-dom";
import DeleteModal from "../../Components/Modals/DeleteModal";

import { BackButton, CardTitle } from "../../Components/CustomComponents";
import styles from "./styles/Inbox.module.scss";

import _ from "lodash";
import { FaTrashAlt } from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import { getMessageTime } from "../../Helper";

const SingleChatScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ticket = location.state.data;
  console.log(ticket);
  const chatContainerRef = useRef(null);
  const popupRef = useRef(null);
  const messageRef = useRef();
  const popoverRef = useRef(null);

  const INITIAL_CHAT_HISTORY = useMemo(() => {
    return {
      isError: false,
      isLoading: true,
      data: [],
    };
  }, []);

  const [chatRoom, setChatRoom] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [user, setUser] = useState({ id: 1, name: "You", image_url: "" }); // ✅ dummy logged-in user

  const [page, setPage] = useState(1);
  const [chatHistory, setChatHistory] = useState(INITIAL_CHAT_HISTORY);
  const [isMessageDeleteModal, setMessageDeleteModal] = useState(false);
  const [media, setMedia] = useState(undefined);
  const [message, setMessage] = useState();

  // ✅ dummy messages inject on mount
  useEffect(() => {
    // fake chatRoom
    setChatRoom({
      id: "room_101",
      room_name: "John Doe",
      image_url: "/images/no_image_available.png",
    });

    // fake messages
    setChatHistory({
      isError: false,
      isLoading: false,
      data: [
        {
          id: 101,
          user_id: 2,
          user_name: "John Doe",
          message: "Hey! How are you?",
          message_timestamp: new Date(Date.now() - 1000 * 60 * 60), // 1 hr ago
        },
        {
          id: 102,
          user_id: 1,
          user_name: "You",
          message: "I’m good, thanks! What about you?",
          message_timestamp: new Date(Date.now() - 1000 * 60 * 45), // 45 mins ago
        },
        {
          id: 103,
          user_id: 2,
          user_name: "John Doe",
          message: "Doing well! Did you check the report I sent?",
          message_timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 mins ago
        },
        {
          id: 104,
          user_id: 1,
          user_name: "You",
          message: "Yes, looks great 👍",
          message_timestamp: new Date(Date.now() - 1000 * 60 * 10), // 10 mins ago
        },
      ],
    });
  }, []);

  const scrollChatContainer = () => {
    const container = document.getElementById("chatContainer");
    if (container) {
      container.scroll({ top: container.scrollHeight, behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollChatContainer();
  }, [chatHistory?.data.length]);

  const handleSendMessage = () => {
    const newMessage = messageRef.current.value;
    if (!newMessage) return;

    const payload = {
      id: Date.now(),
      user_id: user.id,
      user_name: user.name,
      message: newMessage,
      message_timestamp: new Date(),
    };

    setChatHistory((prev) => ({
      ...prev,
      data: [...prev.data, payload],
    }));

    messageRef.current.value = "";
  };

  const handleDeleteMessage = () => {
    setChatHistory((prev) => ({
      ...prev,
      data: prev.data.filter((m) => m.id !== message.id),
    }));
    setMessageDeleteModal(false);
  };

  const handleMessageModalOpen = (item) => {
    setMessageDeleteModal(true);
    setMessage(item);
  };

  const handleMessageModalClose = useCallback(() => {
    setMessageDeleteModal(false);
  }, []);

  return (
    <>
      <BackButton isRelative={true} handleBackButton={() => navigate(-1)} />
      <div className={styles.Inbox}>
        {isMessageDeleteModal && (
          <DeleteModal
            handleDeleteModalClose={handleMessageModalClose}
            onDelete={handleDeleteMessage}
          />
        )}
        <div className={styles.titleContainer}>
          <div className={styles.userContainer}>
            <div className={styles.imageContainer}>
              {isMobile && (
                <span
                  className={styles.backButton}
                  onClick={() => console.log("Back")}
                >
                  <IoIosArrowBack />
                </span>
              )}
              <img
                className={styles.image}
                src={chatRoom?.image_url || "/images/no_image_available.png"}
                alt="User Profile"
              />
              <span className={styles.online}></span>
            </div>
            <div className={styles.nameContainer}>
              <CardTitle
                text={
                  <>
                    {chatRoom?.room_name} -{" "}
                    <small className="bg-danger-subtle text-danger badge pill">
                      {ticket.title}
                    </small>
                  </>
                }
                classes="lc-1 text-white"
              />
              <div className="small">
                {ticket.timeAgo} - Assigned to{" "}
                <b className="bg-success-subtle text-success badge pill">
                  {ticket.assignedTo}
                </b>
              </div>
            </div>
          </div>
        </div>

        <div
          className={styles.chatContainer}
          id="chatContainer"
          ref={chatContainerRef}
        >
          {chatHistory?.isLoading ? (
            <span className={`${styles.noThreads} text-center`}>
              <Spinner variant="success" />
            </span>
          ) : _.isEmpty(chatHistory?.data) ? (
            <p className={`${styles.noThreads} text-center`}>Empty Inbox</p>
          ) : (
            chatHistory?.data.map((item) => {
              const isMe = item?.user_id === user?.id;
              return (
                <div
                  key={item?.id}
                  className={`${styles.chat} ${isMe ? styles.me : ""}`}
                >
                  <span className={styles.info}>
                    {getMessageTime(item?.message_timestamp)}
                  </span>
                  <div className={styles.messageWrapper}>
                    {isMe && (
                      <FaTrashAlt
                        className={styles.deleteIcon}
                        onClick={() => handleMessageModalOpen(item)}
                        title="Delete message"
                      />
                    )}
                    <p className={`${styles.message} ${isMe ? styles.me : ""}`}>
                      {item.message}
                    </p>
                  </div>
                  {isMe && <span className={styles.info}>Seen</span>}
                </div>
              );
            })
          )}
        </div>

        <div className={styles.messageContainer}>
          <Form.Control
            size="lg"
            className={`mt-1 mb-2  ${styles.messageBar}`}
            placeholder={"Type a message"}
            type="text"
            ref={messageRef}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                handleSendMessage();
                e.stopPropagation();
              }
            }}
          />
          <button className={styles.sendButton} onClick={handleSendMessage}>
            <FiSend />
          </button>
        </div>
      </div>
    </>
  );
};

export default SingleChatScreen;
